// How the dashboard turns what the engine found into what a person reads: the categories, the problems
// (one per rule, whatever the number of variants), what their numbers count, the priority order, the
// summary, the filters and the progress of the work. The counting itself lives in @sitenorma/shared
// (problems.ts), which the WordPress plugin's API uses too, so a figure is the same in both; here are the
// parts only the dashboard needs (page sort, filters, global findings). Tested in results-logic.test.ts.

import { PRIORITY_RANK } from '../../../packages/shared/src/priority';
import {
  byPriority,
  type Category,
  categoryOf,
  groupProblems,
  isDelegatedOnly,
  isFullyDiscarded,
  type PageProblems,
  type ProblemFollow,
  problemsByPage,
  type ResultsSummary,
  type Problem as SharedProblem,
  splitCategories,
  summarize,
  tabOf,
  topProblemKeyForPage,
} from '../../../packages/shared/src/problems';
import type { Device, PageIssue, Priority, ScanIssue, ScannedPage, SharedComponent } from './api';

export type { Category, PageProblems, ProblemFollow, ResultsSummary };
export {
  byPriority,
  categoryOf,
  groupProblems,
  isDelegatedOnly,
  isFullyDiscarded,
  PRIORITY_RANK,
  problemsByPage,
  splitCategories,
  summarize,
  tabOf,
  topProblemKeyForPage,
};

export type AnyIssue = ScanIssue | PageIssue;

/** A problem as a person sees it: one rule, once (see @sitenorma/shared). */
export type Problem<T extends AnyIssue = AnyIssue> = SharedProblem<T>;

/** By priority, by name, by the problems pending and by the date of the latest analysis of the page. */
export type PageSort = 'prioridad' | 'nombre' | 'pendientes' | 'fecha';

const pageName = (page: Pick<ScannedPage, 'title' | 'finalUrl'>) => page.title || page.finalUrl;

/**
 * The pages in the order a person works through them. By priority: the pages with the most problems of
 * high priority first, then of medium and of low priority, then the most problems; a page that was not
 * analysed goes last. By name: alphabetical. Either way ties are settled by name, so the order is stable.
 */
export function sortPages<
  T extends Pick<ScannedPage, 'key' | 'title' | 'finalUrl' | 'blocked' | 'error'> & { analysedAt?: string },
>(pages: T[], found: Map<string, PageProblems>, mode: PageSort): T[] {
  const byName = (a: T, b: T) => pageName(a).localeCompare(pageName(b), undefined, { numeric: true });
  if (mode === 'nombre') return [...pages].sort(byName);
  // The most recently analysed first (the date is that of the page's own latest result); a page without one goes last.
  if (mode === 'fecha')
    return [...pages].sort((a, b) => (b.analysedAt ?? '').localeCompare(a.analysedAt ?? '') || byName(a, b));
  // The pages with the most problems pending first (the number of problems, whatever their priority).
  if (mode === 'pendientes')
    return [...pages].sort(
      (a, b) =>
        Number(Boolean(a.blocked || a.error)) - Number(Boolean(b.blocked || b.error)) ||
        (found.get(b.key)?.problems ?? 0) - (found.get(a.key)?.problems ?? 0) ||
        byName(a, b),
    );
  const of = (page: T) => found.get(page.key);
  return [...pages].sort((a, b) => {
    const left = of(a);
    const right = of(b);
    const out = Number(Boolean(a.blocked || a.error)) - Number(Boolean(b.blocked || b.error));
    return (
      out ||
      (right?.byPriority.alta ?? 0) - (left?.byPriority.alta ?? 0) ||
      (right?.byPriority.media ?? 0) - (left?.byPriority.media ?? 0) ||
      (right?.byPriority.baja ?? 0) - (left?.byPriority.baja ?? 0) ||
      (right?.problems ?? 0) - (left?.problems ?? 0) ||
      byName(a, b)
    );
  });
}

/** Where to begin: the problems at the top of the common order, only confirmed ones with a priority. */
export function startHere(problems: Problem[], count = 3): Problem[] {
  return problems
    .filter(
      (problem) =>
        problem.result === 'violation' && problem.priority !== 'pendiente' && !isFullyDiscarded(problem),
    )
    .slice(0, count);
}

export interface Filters {
  priorities: Priority[];
  device: Device | 'todos';
  query: string;
  /** Among the results that are not accessibility problems: best practices, SEO or quality. */
  area: 'todas' | 'bestPractices' | 'seo' | 'quality';
}

/** Filters are only offered for lists this long: with fewer problems they take more room than they save. */
export const FILTER_MINIMUM = 6;

export const NO_FILTERS: Filters = { priorities: [], device: 'todos', query: '', area: 'todas' };

export const hasFilters = (filters: Filters) =>
  filters.priorities.length > 0 ||
  filters.device !== 'todos' ||
  filters.query.trim() !== '' ||
  (filters.area ?? 'todas') !== 'todas';

/** Applies the filters; each one narrows the list and none changes the figures inside a problem. */
export function applyFilters<T extends Problem>(problems: T[], filters: Filters): T[] {
  const query = filters.query.trim().toLocaleLowerCase();
  return problems.filter((problem) => {
    if (filters.priorities.length > 0 && !filters.priorities.includes(problem.priority)) return false;
    if (filters.device !== 'todos' && !problem.devices.includes(filters.device)) return false;
    if ((filters.area ?? 'todas') !== 'todas' && categoryOf(problem) !== filters.area) return false;
    if (!query) return true;
    const haystack = [
      problem.title,
      problem.description ?? '',
      problem.ruleId,
      ...problem.criteria,
      ...problem.criteriaDetail.map((criterion) => criterion.title),
    ]
      .join(' ')
      .toLocaleLowerCase();
    return haystack.includes(query);
  });
}

/** A shared component of a problem: one element that several pages repeat, looked after on one of them. */
export interface GlobalFinding {
  problem: Problem;
  component: SharedComponent;
  ownerKey: string;
  /** `home`: looked after on the home page; `first`: the home page was not one of its pages. */
  ownerKind: 'home' | 'first';
  pageKeys: string[];
}

/**
 * The global problems of a scan, each shared component once, in the common order of the problems: what the
 * section of global problems lists. Discarded problems are not among them, and a component is only global when
 * two or more pages have it.
 */
export function globalFindings(problems: Problem[]): GlobalFinding[] {
  return problems
    .filter((problem) => problem.result === 'violation' && !isFullyDiscarded(problem))
    .flatMap((problem) =>
      problem.shared
        .filter((component) => component.pageKeys.length >= 2 && component.ownerKey)
        .map((component) => ({
          problem,
          component,
          ownerKey: component.ownerKey as string,
          ownerKind: component.ownerKind ?? 'first',
          pageKeys: component.pageKeys,
        })),
    );
}
